export interface PromotionTargetingDisplayRow {
  field: string;
  operator: string;
  value: string;
}

export interface PromotionTargetingGroup {
  id?: number;
  name?: string;
}

function parseGroupId(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return value;
  }

  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    return Number(value.trim());
  }

  return undefined;
}

function normalizedName(name: string | undefined): string {
  return typeof name === 'string' ? name.trim().toLowerCase() : '';
}

function addGroup(
  groups: Map<string, PromotionTargetingGroup>,
  group: PromotionTargetingGroup,
): void {
  if (group.id === undefined && !group.name) {
    return;
  }

  const nameKey = normalizedName(group.name);

  if (group.id !== undefined) {
    const idKey = `id:${group.id}`;
    const existingById = groups.get(idKey);
    const merged: PromotionTargetingGroup = {
      id: group.id,
      name: existingById?.name ?? group.name,
    };

    if (nameKey) {
      groups.delete(`name:${nameKey}`);
    }

    groups.set(idKey, merged);
    return;
  }

  for (const existing of groups.values()) {
    if (normalizedName(existing.name) === nameKey) {
      return;
    }
  }

  groups.set(`name:${nameKey}`, group);
}

function addGroupsFromUnknown(groups: Map<string, PromotionTargetingGroup>, raw: unknown): void {
  if (!raw || typeof raw !== 'object') {
    return;
  }

  const record = raw as Record<string, unknown>;
  addGroup(groups, {
    id: parseGroupId(record.id),
    name: typeof record.name === 'string' && record.name.trim() ? record.name.trim() : undefined,
  });
}

export function isCustomerGroupTargetingField(field: string): boolean {
  const normalized = field.trim().toLowerCase();
  return normalized === 'customer group' || normalized.startsWith('customer group');
}

export function collectPromotionTargetingGroups(
  promotion: Record<string, unknown>,
  targetingDisplay: PromotionTargetingDisplayRow[],
): PromotionTargetingGroup[] {
  const groups = new Map<string, PromotionTargetingGroup>();

  if (Array.isArray(promotion.customer_groups)) {
    for (const raw of promotion.customer_groups) {
      addGroupsFromUnknown(groups, raw);
    }
  }

  addGroupsFromUnknown(groups, promotion.customer_group);

  if (Array.isArray(promotion.target_customer_groups)) {
    for (const raw of promotion.target_customer_groups) {
      addGroupsFromUnknown(groups, raw);
    }
  }

  for (const row of targetingDisplay) {
    if (!isCustomerGroupTargetingField(row.field)) {
      continue;
    }
    const id = parseGroupId(row.value);
    addGroup(groups, {
      id,
      name: id === undefined && row.value.trim() ? row.value.trim() : undefined,
    });
  }

  const uniqueByLabel = new Map<string, PromotionTargetingGroup>();
  for (const group of groups.values()) {
    const label = getPromotionTargetingGroupLabel(group).toLowerCase();
    if (!label) {
      continue;
    }

    const existing = uniqueByLabel.get(label);
    if (!existing) {
      uniqueByLabel.set(label, group);
      continue;
    }

    uniqueByLabel.set(label, {
      id: existing.id ?? group.id,
      name: existing.name ?? group.name,
    });
  }

  return [...uniqueByLabel.values()].sort((left, right) => {
    if (left.id !== undefined && right.id !== undefined) {
      return left.id - right.id;
    }
    if (left.id !== undefined) {
      return -1;
    }
    if (right.id !== undefined) {
      return 1;
    }
    return (left.name ?? '').localeCompare(right.name ?? '');
  });
}

export const PROMOTION_TARGETING_GROUP_SEARCH_THRESHOLD = 10;

export function getPromotionTargetingGroupLabel(group: PromotionTargetingGroup): string {
  if (typeof group.name === 'string' && group.name.trim()) {
    return group.name.trim();
  }
  if (group.id !== undefined) {
    return String(group.id);
  }
  return '';
}

export function matchesPromotionTargetingGroup(
  group: PromotionTargetingGroup,
  query: string,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return true;
  }

  return getPromotionTargetingGroupLabel(group).toLowerCase().includes(needle);
}
